import { useEffect, useMemo, useRef, useState } from 'react';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { useLecturePlayerContext } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.context';
import {
  getActiveTrack,
  getLibLynxMetadata,
  getTrackOptions,
  getVideoAnalyticsAttributes,
} from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.utils';
import { useAnalytics } from '@/context/Analytics';
import { LibraryVideoEvents } from '@/enums/VideoEvent';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useLibLynx } from '@/hooks/useLibLynx';
import { extractPlayerAttributes } from '@/hooks/videojs/videojs-utils';
import { useResource } from '@/lib/datx-jsonapi-react';
import { OriginalsEpisodeTranscripts } from '@/resources/OriginalsEpisodeTranscripts';
import { Transcript } from '@/resources/Transcript';
import { ILibLynxMetadata } from '@/types/LibLynx';

export interface IUseTracksOptionsProps {
  transcripts: Array<Transcript | OriginalsEpisodeTranscripts>;
  activeTrackId: string;
}

export const useTrackOptions = ({ transcripts, activeTrackId }: IUseTracksOptionsProps) => {
  const initialRenderRef = useRef(true);

  const trackOptions = useMemo(
    () => getTrackOptions(transcripts, activeTrackId, { isInitial: initialRenderRef.current, autoCanDefault: true }),
    [transcripts, activeTrackId]
  );

  useEffect(() => {
    initialRenderRef.current = false;
  }, []);

  return trackOptions;
};

export const useLectureVideoAnalytics = () => {
  const { send } = useLibLynx();
  const analytics = useAnalytics();

  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));

  const videoAnalytics = useMemo(() => getVideoAnalyticsAttributes(lecture, lecture?.event), [lecture]);

  const libLynxMetadata: ILibLynxMetadata = useMemo(() => getLibLynxMetadata(lecture), [lecture]);

  return useMemo(() => {
    return {
      onPlay: (videoEvent) => {
        const player = videoEvent.target.player;

        send('trackItemRequest', { im: libLynxMetadata });
        analytics.track(LibraryVideoEvents.Play, { ...videoAnalytics, ...extractPlayerAttributes(player) });
      },
      onResume: (videoEvent) => {
        const player = videoEvent.target.player;

        analytics.track(LibraryVideoEvents.Resume, {
          ...videoAnalytics,
          ...extractPlayerAttributes(player),
        });
      },
      onPause: (videoEvent) => {
        const player = videoEvent.target.player;

        analytics.track(LibraryVideoEvents.Pause, { ...videoAnalytics, ...extractPlayerAttributes(player) });
      },
      onEnded: (videoEvent) => {
        const player = videoEvent.target.player;

        analytics.track(LibraryVideoEvents.Ended, { ...videoAnalytics, ...extractPlayerAttributes(player) });
      },
      onDispose: (videoEvent) => {
        const player = videoEvent.target.player;

        analytics.track(LibraryVideoEvents.Dispose, {
          ...videoAnalytics,
          ...extractPlayerAttributes(player),
        });
      },
    };
  }, [analytics, videoAnalytics, libLynxMetadata, send]);
};

export const usePlayerState = () => {
  const [player] = useLecturePlayerContext();
  const [activeTrack, setActiveTrack] = useState(getActiveTrack(player));
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [cues, setCues] = useState([]);

  useEffect(() => {
    if (!player) return;
    player.on('loadedmetadata', () => {
      setIsLoadingMetadata(false);
    });

    return () => {
      if (!player) return;
      player.off('loadedmetadata', () => {});
    };
  }, [player]);

  useEffect(() => {
    if (!player) return;
    player.on('texttrackchange', setCurrentTrack);

    function setCurrentTrack() {
      const currentlyActiveTrack = getActiveTrack(player);

      setActiveTrack(currentlyActiveTrack);
      setCues(currentlyActiveTrack?.['cues_'] || []);
    }

    return () => {
      if (!player) return;
      player.off('texttrackchange', () => {});
    };
  }, [player]);

  return { activeTrack, cues, isLoadingMetadata };
};
